import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Alert, Badge, Button, Field, Icon, cn } from '@evogard/ui';
import { formatDocument } from '@evogard/domain';
import type { AssistanceVehicle, VehicleSearchResult } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { VEHICLE_SITUATION_FRAME } from '../../components/vehicle-situation.js';

/**
 * Quem vai ser atendido (29/10/2026). A central busca pela placa, pelo nome
 * ou pelo CPF/CNPJ do associado; a busca devolve o associado com os carros
 * dele, e a central escolhe qual. Um carro só na resposta é escolhido sozinho.
 *
 * O carro escolhido aparece num cartão com a situação do ERP na cor que a casa
 * julga (`vehicleSituationTone`): verde ativo, amarelo o que pede atenção,
 * vermelho cancelado. A cor avisa e não bloqueia — quem decide atender um
 * carro cancelado é a central, e regra que ninguém escreveu não vira recusa
 * aqui (regra 14).
 */
export function VehicleLookup({
  text,
  onText,
  vehicle,
  loading,
  error,
  onPick,
  onClear,
}: {
  text: string;
  onText: (text: string) => void;
  vehicle: AssistanceVehicle | null;
  /** O carro escolhido está sendo carregado. */
  loading: boolean;
  error: string | null;
  onPick: (vehicleId: number) => void;
  onClear: () => void;
}) {
  const [results, setResults] = useState<VehicleSearchResult | null>(null);
  const search = useMutation({
    mutationFn: async (q: string) =>
      ok(
        await api.assistanceDispatches.searchVehicles({ query: { q } }),
        200,
        'Não foi possível buscar no cadastro.',
      ),
    onSuccess: (found) => {
      const all = found.flatMap((m) => m.vehicles);
      if (all.length === 1) {
        setResults(null);
        onPick(all[0]!.vehicleId);
      } else setResults(found);
    },
  });
  const term = text.trim();
  const run = () => term.length >= 3 && search.mutate(term);

  if (vehicle) {
    const tone = vehicle.situation?.tone ?? 'neutro';
    return (
      <div
        className={cn(
          'flex flex-wrap items-center gap-4 rounded-xl border p-3',
          VEHICLE_SITUATION_FRAME[tone],
        )}
      >
        <span className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-superficie text-texto-suave">
          <Icon nome="veiculo" size={24} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <b className="font-mono text-lg tracking-wide">
              {vehicle.plate ?? 'Sem placa'}
            </b>
            <Badge tone={tone}>{vehicle.situation?.label ?? 'Sem situação no ERP'}</Badge>
          </div>
          <span className="truncate text-sm">
            {vehicle.label} · <span className="font-mono text-xs">{vehicle.code}</span>
          </span>
          <span className="truncate text-sm font-semibold">
            {vehicle.memberName ?? 'Associado sem nome'}
          </span>
        </div>
        <Button
          variant="secundario"
          size="sm"
          icon="atualizar"
          onClick={() => {
            setResults(null);
            onClear();
          }}
        >
          Trocar
        </Button>
      </div>
    );
  }

  const empty = results !== null && results.every((m) => m.vehicles.length === 0);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <Field
            label="Placa, nome ou CPF do associado"
            required
            value={text}
            onChange={(e) => {
              onText(e.target.value);
              setResults(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                run();
              }
            }}
            help={
              term.length > 0 && term.length < 3
                ? 'Digite pelo menos 3 letras.'
                : undefined
            }
          />
        </div>
        <Button
          icon="busca"
          type="button"
          loading={search.isPending || loading}
          disabled={term.length < 3}
          onClick={run}
        >
          Buscar
        </Button>
      </div>
      {(search.error ?? error) && (
        <Alert tone="erro">{search.error?.message ?? error}</Alert>
      )}
      {empty && <Alert tone="informacao">Nada no cadastro com “{term}”.</Alert>}
      {results && !empty && (
        <div
          className="flex max-h-[24rem] flex-col gap-3 overflow-y-auto pr-1"
          role="list"
          aria-label="Associados encontrados"
        >
          {results
            .filter((m) => m.vehicles.length > 0)
            .map((m) => (
              <div key={m.memberId} role="listitem" className="flex flex-col gap-1.5">
                <span className="text-sm">
                  <b>{m.memberName ?? 'Associado sem nome'}</b>
                  {m.document && (
                    <span className="ml-2 font-mono text-xs text-texto-tenue">
                      {formatDocument(m.document)}
                    </span>
                  )}
                </span>
                <div className="grid gap-2 sm:grid-cols-2">
                  {m.vehicles.map((v) => (
                    <button
                      key={v.vehicleId}
                      type="button"
                      onClick={() => onPick(v.vehicleId)}
                      aria-label={`Escolher ${v.plate ?? v.code}, ${v.label}`}
                      className="flex items-center gap-3 rounded-xl border border-borda bg-superficie p-2.5 text-left text-sm transition-colors hover:border-primaria/50 hover:bg-primaria-suave"
                    >
                      <Icon nome="veiculo" size={20} className="text-texto-suave" />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <b className="font-mono">{v.plate ?? 'Sem placa'}</b>
                        <span className="truncate text-xs text-texto-suave">
                          {v.label} · <span className="font-mono">{v.code}</span>
                        </span>
                      </span>
                      <Badge tone={v.situation?.tone ?? 'neutro'}>
                        {v.situation?.label ?? 'Sem situação'}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
